import { Navigate, Route, useLocation } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  CatalogTable,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import { CatalogImportPage, catalogImportPlugin } from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { ScaffolderFieldExtensions } from '@backstage/plugin-scaffolder-react';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  DefaultTechDocsHome,
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
  type DocsTableRow,
} from '@backstage/plugin-techdocs';
import { EntityRefLinks } from '@backstage/plugin-catalog-react';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import {
  SettingsLayout,
  UserSettingsAuthProviders,
  UserSettingsFeatureFlags,
  UserSettingsGeneral,
  UserSettingsPage,
} from '@backstage/plugin-user-settings';
import { apis } from './apis';
import { signInProviders } from './signInProviders';
import EntityPage from './pages/catalog-entities/index';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';

import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
  SidebarOpenStateProvider,
  SubvalueCell,
  Link,
} from '@backstage/core-components';
import { useState, type ReactNode } from 'react';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { NotificationsPage } from '@backstage/plugin-notifications';
import { SignalsDisplay } from '@backstage/plugin-signals';
import {
  IamPolicyFieldScaffolder,
  JsonInputValidatorFieldScaffolder,
  PlaceholderAwareJsonFieldScaffolder,
  PlaceholderAwareOpenApiFieldScaffolder,
  LambdaIntegrationAutoDetectFieldScaffolder,
  MapRunStateNameAutoDetectFieldScaffolder,
  MapRunStateNamesAutoDetectFieldScaffolder,
  StepFunctionResourcesSummaryFieldScaffolder,
  StepFunctionAdvancedOptionsEditorFieldScaffolder,
  LambdaEditorFieldScaffolder,
  LambdaDefaultsEditorFieldScaffolder,
  LambdaGroupEditorFieldScaffolder,
  ValkyrieLambdaEditorFieldScaffolder,
  ValkyrieLambdaDefaultsEditorFieldScaffolder,
  CondorLambdaEditorFieldScaffolder,
  CondorLambdaDefaultsEditorFieldScaffolder,
  ValkyrieQueueConsumerEditorFieldScaffolder,
  ValkyrieSqsQueueEditorFieldScaffolder,
  LoggedUserFieldScaffolder,
  ApiTypeComboboxFieldScaffolder,
} from 'scaffolder-fields';
import { ConfigEditorPage } from 'config-editor';
import { ReleaseWizardPage, ReleaseOpsPage } from 'release-conductor';
import { AiAssistantContextProvider, AiAssistantWidget } from 'ai-assistant';
import { useApi, configApiRef } from '@backstage/core-plugin-api';
import { BuildInfo } from './components/settings/BuildInfo';
import { McpTokenCard } from './components/settings/McpTokenCard';

const app = createApp({
  apis,
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      // registerComponent intentionally left unbound: hides the "Register Existing Component"
      // button on the scaffolder page (no value in the production product).
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  components: {
    // Committed code offers ONLY AWS Cognito SSO — see signInProviders (guest is a local-only,
    // never-committed edit; CLAUDE.md "Local-dev gotchas"). Keeping guest out of the committed tree
    // means it can never reach beta/prod.
    SignInPage: props => <SignInPage {...props} providers={signInProviders()} />,
  },
});

// Catalog/API list columns: Name, System, Owner, Description only.
// Drops Type + Lifecycle, and Tags (boilerplate tags bloated every row).
// Widths tuned: wide Name so long snake_case names don't wrap, tight
// System/Owner, and a roomy Description.
// defaultKind hides the kind prefix (e.g. "api:") on the name link for that kind.
const makeColumns = (defaultKind: string) => [
  CatalogTable.columns.createTitleColumn({ hidden: true }),
  { ...CatalogTable.columns.createNameColumn({ defaultKind }), width: '38%' },
  { ...CatalogTable.columns.createSystemColumn(), width: '10%' },
  { ...CatalogTable.columns.createOwnerColumn(), width: '16%' },
  { ...CatalogTable.columns.createMetadataDescriptionColumn(), width: '36%' },
];

const catalogColumns = makeColumns('Component');
const apiColumns = makeColumns('api');

// Show 100 rows per page instead of the default 20.
const catalogPagination = { mode: 'offset' as const, limit: 100 };

// TechDocs table: wide Document (title + description), narrow Owner.
// Drops the default Kind + Type columns (mirrors the catalog table).
const docsColumns = [
  {
    title: 'Document',
    field: 'entity.metadata.name',
    highlight: true,
    searchable: true,
    width: '78%',
    render: (row: DocsTableRow) => (
      <SubvalueCell
        value={
          <Link to={row.resolved.docsUrl}>
            {row.entity.metadata.title || row.entity.metadata.name}
          </Link>
        }
        subvalue={row.entity.metadata.description}
      />
    ),
  },
  {
    title: 'Owner',
    field: 'resolved.ownedByRelationsTitle',
    width: '22%',
    render: ({ resolved }: DocsTableRow) => (
      <EntityRefLinks entityRefs={resolved.ownedByRelations} defaultKind="group" />
    ),
  },
];

/*
  A full SettingsLayout replaces the default page, which is the only way to put Integrations
  ahead of the built-in tabs (loose routes are appended after them). The built-in tabs are
  re-declared with the paths the plugin uses so existing links keep working. Each
  SettingsLayout.Route takes exactly one child element.
*/
export const settingsLayout = (
  <SettingsLayout>
    <SettingsLayout.Route path="general" title="General">
      <UserSettingsGeneral />
    </SettingsLayout.Route>
    <SettingsLayout.Route path="integrations" title="Integrations">
      <McpTokenCard />
    </SettingsLayout.Route>
    <SettingsLayout.Route path="auth-providers" title="Authentication Providers">
      <UserSettingsAuthProviders />
    </SettingsLayout.Route>
    <SettingsLayout.Route path="feature-flags" title="Feature Flags">
      <UserSettingsFeatureFlags />
    </SettingsLayout.Route>
    <SettingsLayout.Route path="about" title="About">
      <BuildInfo />
    </SettingsLayout.Route>
  </SettingsLayout>
);

const routes = (
  <FlatRoutes>
    <Route path="/" element={<Navigate to="catalog" />} />
    <Route
      path="/catalog"
      element={
        // Land on all entities, not the default "owned" view — a new dev owns nothing yet, and an
        // empty catalog table reads as "broken / no data".
        <CatalogIndexPage
          key="catalog"
          initiallySelectedFilter="all"
          columns={catalogColumns}
          pagination={catalogPagination}
        />
      }
    />
    <Route
      path="/apis"
      element={
        <CatalogIndexPage
          key="apis"
          initiallySelectedFilter="all"
          columns={apiColumns}
          pagination={catalogPagination}
        />
      }
    />
    <Route
      path="/step-functions"
      element={
        <CatalogIndexPage
          key="step-functions"
          initiallySelectedFilter="all"
          columns={catalogColumns}
          pagination={catalogPagination}
        />
      }
    />
    <Route path="/catalog/:namespace/:kind/:name" element={<CatalogEntityPage />}>
      {EntityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />}>
      <DefaultTechDocsHome columns={docsColumns} />
    </Route>
    <Route path="/docs/:namespace/:kind/:name/*" element={<TechDocsReaderPage />}>
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route
      path="/create"
      element={
        <ScaffolderPage
          templateFilter={entity => {
            const tags = entity.metadata.tags || [];
            return !tags.includes('deprecated');
          }}
          groups={[
            {
              title: 'Your Templates',
              filter: entity => Boolean(entity.metadata.groups),
            },
            {
              title: 'Other Templates',
              filter: entity => !entity.metadata.groups,
            },
          ]}
        />
      }
    >
      <ScaffolderFieldExtensions>
        <IamPolicyFieldScaffolder />
        <JsonInputValidatorFieldScaffolder />
        <PlaceholderAwareJsonFieldScaffolder />
        <PlaceholderAwareOpenApiFieldScaffolder />
        <LambdaIntegrationAutoDetectFieldScaffolder />
        <MapRunStateNameAutoDetectFieldScaffolder />
        <MapRunStateNamesAutoDetectFieldScaffolder />
        <StepFunctionResourcesSummaryFieldScaffolder />
        <StepFunctionAdvancedOptionsEditorFieldScaffolder />
        <LambdaEditorFieldScaffolder />
        <LambdaDefaultsEditorFieldScaffolder />
        <LambdaGroupEditorFieldScaffolder />
        <ValkyrieLambdaEditorFieldScaffolder />
        <ValkyrieLambdaDefaultsEditorFieldScaffolder />
        <CondorLambdaEditorFieldScaffolder />
        <CondorLambdaDefaultsEditorFieldScaffolder />
        <ValkyrieQueueConsumerEditorFieldScaffolder />
        <ValkyrieSqsQueueEditorFieldScaffolder />
        <LoggedUserFieldScaffolder />
        <ApiTypeComboboxFieldScaffolder />
      </ScaffolderFieldExtensions>
    </Route>
    <Route path="/api-docs" element={<ApiExplorerPage columns={catalogColumns} />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />}>
      {settingsLayout}
    </Route>
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/notifications" element={<NotificationsPage />} />
    <Route path="/config-editor" element={<ConfigEditorPage />} />
    <Route path="/release-conductor" element={<ReleaseWizardPage />} />
    <Route path="/release-ops" element={<ReleaseOpsPage />} />
  </FlatRoutes>
);

const RootWithSidebarState = ({ children }: { children: ReactNode }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  return (
    <SidebarOpenStateProvider value={{ isOpen: sidebarOpen, setOpen: setSidebarOpen }}>
      <Root>{children}</Root>
    </SidebarOpenStateProvider>
  );
};

// AI config copilot (M3) — rendered only when aiAssistant.enabled is true. The static createRoot tree
// cannot read config directly (no hooks), so the flag is read here inside a component.
// Show the copilot only when it's enabled AND we're not on the scaffolder wizard (/create...): the
// fixed Fab sits bottom-right on top of the stepper's Next/Create button, and the copilot only drives
// the config-editor, not scaffolding.
export const shouldShowAiWidget = (enabled: boolean, pathname: string): boolean =>
  enabled && pathname !== '/create' && !pathname.startsWith('/create/');

const AiAssistantWidgetGate = () => {
  const configApi = useApi(configApiRef);
  const { pathname } = useLocation();
  const enabled = configApi.getOptionalBoolean('aiAssistant.enabled') ?? false;
  if (!enabled) return null;
  // Always mount the widget so its <Coachmark> overlay renders during a create-drive (which navigates
  // to /create/templates/...). Only the launcher Fab/panel is hidden on the scaffolder wizard, where it
  // would sit on top of the stepper's Next/Create button.
  return <AiAssistantWidget showLauncher={shouldShowAiWidget(enabled, pathname)} />;
};

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <SignalsDisplay />
    <AppRouter>
      <AiAssistantContextProvider>
        <RootWithSidebarState>{routes}</RootWithSidebarState>
        <AiAssistantWidgetGate />
      </AiAssistantContextProvider>
    </AppRouter>
  </>,
);
